import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { formatDate, formatEuro, formatInteger, formatSquareMeters } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Eyebrow, ImageSlot, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { ReleaseReservationDialog, ReserveUnitDialog } from '../../reservations';
import {
  remindReservation,
  useDealAppointments,
  useInvalidateProperty,
  useReservationItem,
  useUnit,
} from '../api';
import {
  CardHead,
  Crumbs,
  DashedButton,
  DocumentRows,
  EyebrowInline,
  FieldPair,
  SourceBadge,
  StatusPill,
  TinyButton,
  documentRowItem,
} from '../bits';
import { availabilityPill } from '../availability';
import { dateWithTime, shortDateTime } from '../format';
import { MediaGallery } from '../media/MediaGallery';
import { PICTURE_SIZES, coverItem, floorPlanItem, pictureOf } from '../media/gallery';
import type { DealAppointmentItem, UnitDetail } from '../types';
import { OwnerUploadDialog } from '../record/OwnerUploadDialog';
import { rentFactRows } from '../rent';
import { CostItemsCard } from './CostItemsCard';
import { TenancyCard } from './TenancyCard';
import {
  AmenitiesEnergyCard,
  ExternalReservationsCard,
  LinkedUnitsCard,
  PriceComposition,
} from './UnitDataCards';
import { UnitEditDialog, type UnitEditTab } from './UnitEditDialog';
import { calculatorFactRows, extendedFactRows } from './unitData';
import styles from './UnitRecordPage.module.css';

type DialogKey = 'edit' | 'upload' | 'reserve' | 'release' | null;

function PriceRow({ label, cents }: { label: string; cents: number | null }) {
  return (
    <div className={styles.priceRow}>
      <span className={styles.priceLabel}>{label}</span>
      <span className={styles.priceValue}>{cents === null ? '—' : formatEuro(cents)}</span>
    </div>
  );
}

function monthly(cents: number | null): string {
  return cents === null ? '—' : `${formatEuro(cents)} mtl.`;
}

/** The next booked appointment of the deal (Objekte 06 „Videotermin" highlight), or null. */
function upcomingAppointment(
  items: DealAppointmentItem[] | undefined,
  now: number,
): DealAppointmentItem | null {
  if (!items) return null;
  const booked = items
    .filter(
      (item) =>
        (item.status === 'booked' || item.status === 'rescheduled') && Date.parse(item.startsAt) > now,
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  return booked[0] ?? null;
}

function unitChips(unit: UnitDetail): string[] {
  const chips: string[] = [];
  chips.push(
    unit.fields.areaSqm === null
      ? unit.number
      : `${unit.number} · ${formatSquareMeters(unit.fields.areaSqm)}`,
  );
  if (unit.price.priceCents !== null) chips.push(formatEuro(unit.price.priceCents));
  if (unit.price.parkingSpaceNumber && unit.price.parkingSpacePriceCents !== null) {
    chips.push(`${unit.price.parkingSpaceNumber} · ${formatEuro(unit.price.parkingSpacePriceCents)}`);
  }
  if (unit.price.serviceChargeCents !== null)
    chips.push(`Hausgeld ${formatEuro(unit.price.serviceChargeCents)}`);
  return chips;
}

/** Why a free unit cannot be reserved (sales release, TeamProQ), worded like the API's 409 messages. */
function blockedIntro(unit: UnitDetail): string | null {
  switch (unit.availability) {
    case 'not_released':
      return 'Diese Einheit ist im Vertrieb nicht freigegeben.';
    case 'external_reserved':
      return 'Diese Einheit ist in TeamProQ reserviert.';
    case 'external_exclusive':
      return 'Diese Einheit ist in TeamProQ exklusiv reserviert.';
    case 'external_sold':
      return 'Diese Einheit ist in TeamProQ verkauft.';
    case 'available':
    case 'reserved':
    case 'sold':
    case 'unavailable':
      return null;
  }
}

/** Objekte 04 (free) and 06 (reserved) at `/properties/:id/units/:unitId`. */
export function UnitRecordPage() {
  const { id: propertyId = '', unitId = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidate = useInvalidateProperty();
  const unit = useUnit(unitId);
  const reservationId = unit.data?.reservation?.id ?? null;
  const reservation = useReservationItem(reservationId);
  const dealId = unit.data?.reservation?.deal.id ?? null;
  const appointments = useDealAppointments(dealId);
  const [dialog, setDialog] = useState<DialogKey>(null);
  const [editTab, setEditTab] = useState<UnitEditTab>('location');
  const [reminding, setReminding] = useState(false);
  const now = useMemo(() => Date.now(), []);

  const refresh = () => {
    void invalidate(propertyId);
    void unit.refetch();
    void reservation.refetch();
  };

  const crumbs = (propertyName: string, current: string) => (
    <Crumbs
      items={[
        { label: 'Objekte', href: '/properties' },
        { label: propertyName, href: `/properties/${propertyId}` },
        { label: current },
      ]}
    />
  );

  if (unit.isPending) {
    return (
      <div className={styles.page} aria-busy="true">
        {crumbs('Objekt', 'wird geladen')}
        <div className={styles.headSkeleton}>
          <Skeleton width={80} height={22} />
          <Skeleton width={340} height={32} />
          <Skeleton width={420} height={14} />
        </div>
        <div className={styles.grid}>
          <div className={styles.column}>
            <div className={styles.images}>
              <Skeleton variant="block" height={260} />
              <Skeleton variant="block" height={260} />
            </div>
            <Skeleton variant="block" height={220} />
            <Skeleton variant="block" height={300} />
          </div>
          <div className={styles.column}>
            <Skeleton variant="block" height={360} />
            <Skeleton variant="block" height={280} />
          </div>
        </div>
      </div>
    );
  }
  if (unit.isError) {
    const notFound = unit.error instanceof ApiError && unit.error.status === 404;
    return (
      <div className={styles.page}>
        {crumbs('Objekt', notFound ? 'nicht gefunden' : 'Fehler')}
        <Banner
          tone={notFound ? 'neutral' : 'danger'}
          title={
            notFound ? 'Die Einheit wurde nicht gefunden.' : 'Die Einheitenakte konnte nicht geladen werden.'
          }
          actions={
            <>
              <Button
                variant="secondary"
                size="small"
                onClick={() => void navigate(`/properties/${propertyId}`)}
              >
                Zur Objektakte
              </Button>
              {notFound ? null : (
                <Button variant="ghost" size="small" onClick={() => void unit.refetch()}>
                  Erneut versuchen
                </Button>
              )}
            </>
          }
        >
          {notFound ? 'Der Link zeigt auf keine Einheit in Ihrem Bereich.' : errorMessage(unit.error)}
        </Banner>
      </div>
    );
  }

  const data = unit.data;
  const reserved = data.reservation !== null;
  const item = reservation.data ?? null;
  const upcoming = upcomingAppointment(appointments.data?.items, now);
  const canRemind = item ? item.canRemind : data.reservation?.status === 'awaiting_signature';
  const canRelease = item ? item.canRelease || item.canRequestRelease : false;

  /** „Bearbeiten" of a card opens the dialog on the card's tab (D-367). */
  function edit(tab: UnitEditTab) {
    setEditTab(tab);
    setDialog('edit');
  }

  async function remind() {
    if (!data.reservation) return;
    setReminding(true);
    try {
      await remindReservation(data.reservation.id);
      toast({ message: 'Erinnerung gesendet.' });
      refresh();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setReminding(false);
    }
  }

  const subtitle =
    reserved && data.reservation
      ? `${data.propertyName} · ${data.data.buildingFloor} · reserviert für ${data.reservation.contact.name}`
      : data.subtitle;

  const rentRows = rentFactRows({
    actualCents: data.fields.rentActualCents,
    targetCents: data.fields.rentTargetCents,
    subsidyCents: data.fields.rentSubsidyCents,
    subsidyCycles: data.fields.rentSubsidyCycles,
  }).map((row) => <FieldPair key={row.label} label={row.label} value={row.value} />);
  const documents = data.documents.items.map(documentRowItem);
  const deliveries = item?.deliveries ?? [];
  const availability = availabilityPill(data.availability);
  const floorPlan = floorPlanItem(data.media);
  const cover = coverItem(data.media);
  const view = cover && cover.id !== floorPlan?.id ? cover : null;
  const floorPlanPicture = floorPlan ? pictureOf(floorPlan.fileId, { size: floorPlan }) : null;
  const viewPicture = view ? pictureOf(view.fileId, { size: view }) : null;
  const extraRows = [
    ...extendedFactRows(data.fields, data.external !== null),
    ...calculatorFactRows(data.fields),
  ].map((row) => <FieldPair key={row.label} label={row.label} value={row.value} />);
  const blocked = blockedIntro(data);

  return (
    <div className={styles.page}>
      {crumbs(data.propertyName, data.number)}
      <div className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.headTop}>
            <Eyebrow>Einheit</Eyebrow>
            <StatusPill tone={data.statusTone} unit>
              {data.statusPill}
            </StatusPill>
            {availability.label !== data.statusPill ? (
              <StatusPill tone={availability.tone}>{availability.label}</StatusPill>
            ) : null}
            {data.external ? <SourceBadge external={data.external} /> : null}
          </div>
          <h1 className={styles.title}>{data.title}</h1>
          <div className={styles.subtitle}>{subtitle}</div>
        </div>
        <div className={styles.headActions}>
          {reserved ? (
            <>
              {canRemind ? (
                <button
                  type="button"
                  className={clsx('pressable', styles.headButton)}
                  onClick={() => void remind()}
                  disabled={reminding}
                  aria-busy={reminding || undefined}
                >
                  Erinnerung senden
                </button>
              ) : null}
              {canRelease ? (
                <button
                  type="button"
                  className={clsx('pressable', styles.headButton, styles.headButtonDanger)}
                  onClick={() => setDialog('release')}
                >
                  Reservierung aufheben
                </button>
              ) : null}
            </>
          ) : data.canEdit ? (
            <button
              type="button"
              className={clsx('pressable', styles.headButton)}
              onClick={() => edit('location')}
            >
              Einheit bearbeiten
            </button>
          ) : null}
          {data.actions.calculator ? (
            <button
              type="button"
              className={clsx('pressable', styles.headButton, styles.headButtonAccent)}
              onClick={() => void navigate(`/properties/${propertyId}/calculator?unitId=${unitId}`)}
            >
              Rechner starten
            </button>
          ) : null}
          {reserved && data.reservation ? (
            <Button
              className={styles.headPrimary}
              onClick={() => void navigate(`/deals/${data.reservation?.deal.id ?? ''}`)}
            >
              Zum Deal
            </Button>
          ) : data.actions.reserve === 'available' ? (
            <Button className={styles.headPrimary} onClick={() => setDialog('reserve')}>
              Einheit reservieren
            </Button>
          ) : null}
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.column}>
          <div className={clsx(styles.images, reserved && styles.imagesReserved)}>
            <ImageSlot
              chrome="frame"
              src={floorPlanPicture?.src}
              srcSet={floorPlanPicture?.srcSet}
              sizes={PICTURE_SIZES.unitSlot}
              fit="contain"
              alt={floorPlan?.caption ?? `Grundriss ${data.number}`}
              placeholder={`Grundriss ${data.number}`}
              kind="plan"
              height={reserved ? 220 : 260}
              radius="card"
              className={styles.imageSlot}
            />
            <ImageSlot
              chrome="frame"
              src={viewPicture?.src}
              srcSet={viewPicture?.srcSet}
              sizes={PICTURE_SIZES.unitSlot}
              alt={view?.caption ?? 'Ansicht / Visualisierung'}
              placeholder="Ansicht / Visualisierung"
              height={reserved ? 220 : 260}
              radius="card"
              className={styles.imageSlot}
            />
          </div>

          <section className={styles.card} aria-labelledby="unit-data">
            {reserved ? (
              <EyebrowInline id="unit-data">Einheitendaten</EyebrowInline>
            ) : (
              <CardHead
                eyebrow="Einheitendaten"
                id="unit-data"
                end={
                  data.canEdit ? (
                    <span className={styles.cardHeadActions}>
                      wird in Reservierung und Kaufvertrag übernommen
                      <TinyButton onClick={() => edit('location')}>Lage bearbeiten</TinyButton>
                      <TinyButton onClick={() => edit('rent')}>Miete bearbeiten</TinyButton>
                    </span>
                  ) : (
                    'wird in Reservierung und Kaufvertrag übernommen'
                  )
                }
              />
            )}
            <div className={styles.dataGrid}>
              <FieldPair label="Bezeichnung" value={data.data.number} />
              <FieldPair label="Haus / Etage" value={data.data.buildingFloor} />
              <FieldPair label="Zimmer" value={data.data.rooms} />
              <FieldPair label="Wohnfläche" value={data.data.area} />
              {reserved ? (
                <>
                  <FieldPair
                    label="Kaufpreis"
                    value={data.price.priceCents === null ? '—' : formatEuro(data.price.priceCents)}
                  />
                  <FieldPair label="Stellplatz" value={data.data.parkingSpace} />
                  <FieldPair label="Hausgeld" value={monthly(data.price.serviceChargeCents)} />
                  <FieldPair label="Bezugsfertig" value={data.data.readyForOccupancy} />
                  {rentRows}
                  {extraRows}
                </>
              ) : (
                <>
                  <FieldPair label="Ausrichtung" value={data.data.orientation} />
                  <FieldPair label="Balkon / Terrasse" value={data.data.balcony} />
                  <FieldPair label="Stellplatz" value={data.data.parkingSpace} />
                  <FieldPair label="Keller" value={data.data.cellar} />
                  <FieldPair label="Miteigentumsanteil" value={data.data.coOwnershipShare} />
                  <FieldPair label="Bezugsfertig" value={data.data.readyForOccupancy} />
                  <FieldPair label="Ausstattung" value={data.data.fittings} />
                  <FieldPair label="Vermietet" value={data.data.rented} />
                  {rentRows}
                  {extraRows}
                </>
              )}
            </div>
          </section>

          {reserved ? (
            <section className={styles.card} aria-labelledby="unit-history">
              <EyebrowInline id="unit-history">Verlauf</EyebrowInline>
              {upcoming && data.reservation ? (
                <div className={styles.appointment}>
                  <div className={styles.appointmentTop}>
                    <span className={styles.appointmentKind}>{upcoming.typeName}</span>
                    <span className={styles.appointmentChip}>
                      Deal {data.reservation.contact.name.split(' ').slice(-1)[0]}
                    </span>
                    <span className={styles.appointmentDate}>{upcoming.dateLabel}</span>
                  </div>
                  <div className={styles.appointmentTitle}>
                    {upcoming.typeName} Nr. {formatInteger(upcoming.number)} · {data.reservation.contact.name}
                  </div>
                  <div className={styles.appointmentMeta}>
                    {[upcoming.heldBy?.shortName ?? upcoming.bookedBy?.shortName, upcoming.statusLabel]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  <div className={styles.appointmentActions}>
                    <Link
                      to={`/calendar/appointments/${upcoming.id}`}
                      className={clsx('pressable', styles.appointmentButton)}
                    >
                      Termin öffnen
                    </Link>
                  </div>
                </div>
              ) : null}
              <div className={styles.history}>
                {reservation.isPending ? (
                  <Skeleton width="60%" height={12} />
                ) : item && item.history.length > 0 ? (
                  [...item.history].reverse().map((row, index) => (
                    <div key={`${row.at}-${index}`} className={styles.historyRow}>
                      <span className={styles.historyTime}>{shortDateTime(row.at)}</span>
                      <span className={styles.historyText}>
                        {row.title}
                        {row.byShort ? ` · ${row.byShort}` : ''}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className={styles.emptyText}>Noch kein Verlauf.</p>
                )}
              </div>
            </section>
          ) : (
            <section className={styles.card} aria-labelledby="unit-price">
              <CardHead
                eyebrow="Preis und Nebenkosten"
                id="unit-price"
                end={
                  data.canEdit ? (
                    <TinyButton onClick={() => edit('calculation')}>Bearbeiten</TinyButton>
                  ) : undefined
                }
              />
              <div className={styles.priceGrid}>
                <PriceRow label="Kaufpreis Wohnung" cents={data.price.priceCents} />
                <div className={styles.priceRow}>
                  <span className={styles.priceLabel}>Hausgeld</span>
                  <span className={styles.priceValue}>{monthly(data.price.serviceChargeCents)}</span>
                </div>
                <PriceRow
                  label={
                    data.price.parkingSpaceNumber
                      ? `Stellplatz ${data.price.parkingSpaceNumber}`
                      : 'Stellplatz'
                  }
                  cents={data.price.parkingSpacePriceCents}
                />
                <div className={styles.priceRow}>
                  <span className={styles.priceLabel}>davon Instandhaltungsrücklage</span>
                  <span className={styles.priceValue}>{monthly(data.price.reserveShareCents)}</span>
                </div>
                <PriceRow label="Preis je m²" cents={data.price.pricePerSqmCents} />
                <PriceRow label={data.price.labels.landTransferTax} cents={data.price.landTransferTaxCents} />
                <PriceRow
                  label={data.price.labels.notaryLandRegistry}
                  cents={data.price.notaryLandRegistryCents}
                />
                <PriceRow label={data.price.labels.commission} cents={data.price.commissionCents} />
              </div>
              <div className={styles.priceTotal}>
                <span className={styles.priceTotalLabel}>{data.price.labels.total}</span>
                <span className={styles.priceTotalValue}>
                  {data.price.totalInvestmentCents === null
                    ? '—'
                    : formatEuro(data.price.totalInvestmentCents)}
                </span>
              </div>
              <PriceComposition unit={data} />
            </section>
          )}

          <AmenitiesEnergyCard unit={data} />
          <CostItemsCard unit={data} editable={data.canEdit} onChanged={refresh} />
          <MediaGallery
            owner={{ unitId }}
            items={data.media}
            canEdit={data.canEdit}
            external={data.external}
            headingId="unit-media"
            onChanged={refresh}
          />
        </div>

        <div className={styles.column}>
          {reserved && data.reservation ? (
            <section className={clsx(styles.card, styles.cardAccent)} aria-labelledby="unit-reservation">
              <div className={styles.cardHead}>
                <Eyebrow id="unit-reservation">Reservierung</Eyebrow>
                <span className={styles.statusTag}>{data.reservation.statusLabel}</span>
              </div>
              <div className={styles.reservationContact}>
                <span className={styles.contactAvatar} aria-hidden="true">
                  {data.reservation.contact.initials}
                </span>
                <div>
                  <div className={styles.contactName}>
                    <Link to={`/contacts/${data.reservation.contact.id}`} className={styles.contactLink}>
                      {data.reservation.contact.name}
                    </Link>
                  </div>
                  <div className={styles.contactEmail}>{data.reservation.contact.email ?? '—'}</div>
                </div>
              </div>
              <div className={styles.reservationRows}>
                <div className={styles.reservationRow}>
                  <span className={styles.reservationLabel}>Gesendet</span>
                  <span className={styles.reservationValue}>
                    {data.reservation.sentAt ? dateWithTime(data.reservation.sentAt) : 'vor Ort'}
                  </span>
                </div>
                <div className={styles.reservationRow}>
                  <span className={styles.reservationLabel}>Link gültig bis</span>
                  <span className={styles.reservationValue}>
                    {data.reservation.validUntil ? formatDate(data.reservation.validUntil) : '—'}
                  </span>
                </div>
                <div className={styles.reservationRow}>
                  <span className={styles.reservationLabel}>Status</span>
                  <span className={clsx(styles.reservationValue, styles.reservationAccent)}>
                    {data.reservation.statusText}
                  </span>
                </div>
                <div className={styles.reservationRow}>
                  <span className={styles.reservationLabel}>Deal</span>
                  <span className={styles.reservationValue}>
                    <Link to={`/deals/${data.reservation.deal.id}`} className={styles.contactLink}>
                      {data.reservation.deal.label}
                    </Link>
                  </span>
                </div>
              </div>
              <div className={styles.cardButtons}>
                <a
                  className={clsx('pressable', styles.cardButton)}
                  href={`/api/reservations/${data.reservation.id}/agreement.pdf`}
                  target="_blank"
                  rel="noopener"
                >
                  Vereinbarung ansehen
                </a>
                {canRemind ? (
                  <button
                    type="button"
                    className={clsx('pressable', styles.cardButton)}
                    onClick={() => void remind()}
                    disabled={reminding}
                    aria-busy={reminding || undefined}
                  >
                    Erneut senden
                  </button>
                ) : null}
              </div>
            </section>
          ) : (
            <section className={clsx(styles.card, styles.cardAccent)} aria-labelledby="unit-reservation">
              <CardHead
                eyebrow="Reservierung"
                id="unit-reservation"
                end={
                  data.canEdit ? (
                    <TinyButton onClick={() => edit('status')}>Status bearbeiten</TinyButton>
                  ) : undefined
                }
              />
              <p className={styles.reservationIntro}>
                {data.actions.reserve === 'available'
                  ? 'Diese Einheit ist frei. Ordnen Sie einen Kontakt zu, dann geht die Reservierungsvereinbarung mit den Angaben dieser Einheit an den Kunden zur digitalen Unterschrift.'
                  : blocked
                    ? `${blocked} Eine Reservierung ist derzeit nicht möglich.`
                    : data.status === 'sold'
                      ? 'Diese Einheit ist verkauft. Eine Reservierung ist nicht mehr möglich.'
                      : data.status === 'unavailable'
                        ? 'Diese Einheit ist nicht verfügbar.'
                        : 'Diese Einheit ist frei. Reservierungen sind erst nach der Vertriebsfreigabe des Objekts möglich.'}
              </p>
              <div className={styles.chipsLabel}>Übernommene Angaben</div>
              <div className={styles.chips}>
                {unitChips(data).map((chip) => (
                  <span key={chip} className={styles.chip}>
                    {chip}
                  </span>
                ))}
              </div>
              <div className={styles.cardButtons}>
                {data.actions.reserve === 'available' ? (
                  <Button block className={styles.cardPrimary} onClick={() => setDialog('reserve')}>
                    Reservierung senden
                  </Button>
                ) : null}
                <a
                  className={clsx('pressable', styles.cardButton)}
                  href={`/api/units/${unitId}/agreement-preview.pdf`}
                  target="_blank"
                  rel="noopener"
                >
                  Vorschau ansehen
                </a>
              </div>
            </section>
          )}

          <section className={styles.card} aria-labelledby="unit-documents">
            <CardHead
              eyebrow="Unterlagen zur Einheit"
              id="unit-documents"
              end={
                reserved
                  ? 'mit Reservierung versendet'
                  : `${formatInteger(data.documents.total)} ${data.documents.total === 1 ? 'Dokument' : 'Dokumente'}`
              }
            />
            {reserved ? (
              reservation.isPending ? (
                <div className={styles.docsSkeleton}>
                  <Skeleton width="70%" height={13} />
                  <Skeleton width="55%" height={13} />
                </div>
              ) : (
                <DocumentRows
                  items={deliveries.map((delivery, index) => ({
                    key: `${delivery.name}-${index}`,
                    name: delivery.name,
                    meta: delivery.status,
                    tone: delivery.status === 'wartet' ? ('accent' as const) : ('default' as const),
                  }))}
                  emptyText="Die Vereinbarung wurde vor Ort unterschrieben, es ging nichts per E-Mail hinaus."
                />
              )
            ) : (
              <>
                <DocumentRows items={documents} emptyText="Noch keine Unterlagen zur Einheit." />
                <div className={styles.note}>{data.documents.note}</div>
                {data.canEdit ? (
                  <DashedButton onClick={() => setDialog('upload')}>Unterlage hochladen</DashedButton>
                ) : null}
              </>
            )}
          </section>

          <TenancyCard unit={data} editable={data.canEdit} onChanged={refresh} />
          <LinkedUnitsCard unit={data} editable={data.canEdit} onChanged={refresh} />
          <ExternalReservationsCard unit={data} />
        </div>
      </div>

      <UnitEditDialog
        open={dialog === 'edit'}
        unit={data}
        initialTab={editTab}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          toast({ message: 'Einheit gespeichert.' });
          refresh();
        }}
      />
      <OwnerUploadDialog
        open={dialog === 'upload'}
        owner={{ unitId }}
        eyebrow="Unterlagen zur Einheit"
        title="Unterlage hochladen"
        onClose={() => setDialog(null)}
        onUploaded={(row) => {
          setDialog(null);
          toast({ message: `Hochgeladen: ${row.name}. Die Datei wird geprüft.` });
          refresh();
        }}
      />
      {dialog === 'reserve' ? (
        <ReserveUnitDialog
          open
          unitId={unitId}
          onClose={() => setDialog(null)}
          onReserved={() => {
            setDialog(null);
            refresh();
          }}
        />
      ) : null}
      {dialog === 'release' && data.reservation ? (
        <ReleaseReservationDialog
          open
          reservationId={data.reservation.id}
          onClose={() => setDialog(null)}
          onReleased={() => {
            setDialog(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
